// ============================================================================
// THE MOTION SYSTEM — docs/design/motion-brief.md is the contract.
//
// "One shared `motion.ts` exporting the five tokens in §2. No per-component
// timing literals." (motion-brief §4). This file is that one place. Every
// duration and every easing curve in the web app resolves here or to the
// matching CSS custom property in index.css — the CSS half exists so a pure-CSS
// transition (the hover/press/focus layer) reads the SAME numbers rather than a
// second copy of them that drifts.
//
// WHAT MOTION IS FOR (§1). Exactly two jobs: hover says WHERE SOMETHING CAME
// FROM, press and state-change confirm SOMETHING HAPPENED. Nothing celebrates.
// If a motion answers neither question, it does not ship. The corollary that
// matters more than it sounds: an animation that resolves into nothing is
// legitimate — a withheld grade shimmers forever and never fills (13f), because
// faking a resolution would be the app faking knowledge.
//
// REDUCED MOTION IS NOT "NO FEEDBACK" (§2, non-negotiable). It collapses every
// transition to the colour change at 0ms. The state still changes; it just does
// not travel. `runSequence` therefore jumps a §3 moment straight to its END
// STATE rather than skipping the moment — the row still leaves, the count still
// rolls, the value still swaps.
// ============================================================================

/* ── §2 TIMING TOKENS ──────────────────────────────────────────────────────
   The five that ship, verbatim from the brief's table. Two further easings
   (`--ease-out`, `--ease-in-out`) predate this system and stay in index.css as
   reserved curves; nothing new should reach for them. `EASE_SETTLE` is the
   brief's third curve — listed there as reserved, and 13b's checkmark pop and
   13e's citation chips are the two moments that spend it. */

/** Hover fills, streaming, value swaps. */
export const EASE_INK = 'cubic-bezier(.3,0,.2,1)'
/** Press, collapse, exits. */
export const EASE_PRESS = 'cubic-bezier(.2,0,.3,1)'
/** Overshoot, spent only on the checkmark pop (13b) and citation chips (13e). */
export const EASE_SETTLE = 'cubic-bezier(.34,1.4,.5,1)'

/** Ink fill, in ms. */
export const DUR_HOVER = 180
/** Press compression, in ms. */
export const DUR_PRESS = 90
/** Colour / border / shadow, in ms. */
export const DUR_STATE = 120

/** Every §2 token, keyed by the CSS custom property that carries the same value. */
export const MOTION_TOKENS = {
  '--ease-ink': EASE_INK,
  '--ease-press': EASE_PRESS,
  '--ease-settle': EASE_SETTLE,
  '--dur-hover': `${DUR_HOVER}ms`,
  '--dur-press': `${DUR_PRESS}ms`,
  '--dur-state': `${DUR_STATE}ms`,
} as const

/* ── THE REDUCED-MOTION GUARD ────────────────────────────────────────────── */

/**
 * True when the OS asks for reduced motion.
 *
 * Defensive on purpose: `matchMedia` is absent in some test environments and in
 * SSR, and a motion helper that throws would take a whole screen down with it.
 * The safe default is "motion is fine" — the CSS layer independently collapses
 * transitions under the same query, so a false negative here costs a §3
 * sequence, never the state change behind it.
 */
export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  } catch {
    return false
  }
}

/* ── THE SEQUENCER ───────────────────────────────────────────────────────── */

/** One step of a §3 moment: a target, its keyframes, and when it runs. */
export interface MotionStep {
  /** The element to animate. Null steps are skipped — a moment whose surface is not mounted is not an error. */
  target: Element | null | undefined
  keyframes: Keyframe[]
  duration: number
  /** ms after the sequence starts. Default 0. */
  delay?: number
  easing?: string
  /** Default `forwards` — a §3 moment ends in the state it moved to, never snapped back. */
  fill?: FillMode
  /** `transform-origin` etc. applied for the life of the step (13d's top-origin growth). */
  origin?: string
}

/** The handle a caller uses to abandon a moment (an unmount mid-sequence). */
export interface MotionRun {
  /** Resolves when the last step has finished — or immediately, under reduced motion. */
  finished: Promise<void>
  cancel: () => void
}

/**
 * Run a §3 moment.
 *
 * UNDER REDUCED MOTION this does not "skip the animation" — it commits every
 * step's LAST keyframe immediately and resolves. The row still ends collapsed,
 * the value still ends swapped, the count still ends rolled. Removing the
 * feedback is the one thing the brief forbids.
 *
 * Also the fallback path when `Element.animate` is missing (jsdom): same
 * treatment, same end state, so a unit test asserting the outcome of a moment
 * does not have to own a WAAPI polyfill.
 */
export function runSequence(steps: MotionStep[]): MotionRun {
  const live = steps.filter((s): s is MotionStep & { target: Element } => Boolean(s.target))

  if (prefersReducedMotion() || !supportsWebAnimations(live)) {
    for (const step of live) commitEndState(step)
    return { finished: Promise.resolve(), cancel: () => {} }
  }

  const animations: Animation[] = []
  for (const step of live) {
    if (step.origin && (step.target instanceof HTMLElement || step.target instanceof SVGElement)) {
      step.target.style.transformOrigin = step.origin
    }
    animations.push(
      step.target.animate(step.keyframes, {
        duration: step.duration,
        delay: step.delay ?? 0,
        easing: step.easing ?? EASE_INK,
        fill: step.fill ?? 'forwards',
      }),
    )
  }

  // THE WATCHDOG, and it is not belt-and-braces — it is the contract.
  //
  // Callers gate REAL WORK on `finished`: 13b writes the completion when the row has finished
  // collapsing. A Web Animation only advances while the document is rendering, so in a tab that
  // is hidden, backgrounded, throttled, or (found the hard way) driven by an automation pane that
  // is not compositing frames, `animation.finished` never resolves — and the owner's task is
  // never marked done. A motion system that can swallow a write is worse than no motion system.
  //
  // So the sequence resolves on whichever comes first: the animations actually finishing, or the
  // wall clock passing the point where they should have. The animation is the garnish; the state
  // change is the meal, and it is never allowed to depend on a frame being painted.
  const wallClock = live.reduce((max, s) => Math.max(max, (s.delay ?? 0) + s.duration), 0) + 250

  let timer: ReturnType<typeof setTimeout> | undefined
  const finished = Promise.race([
    Promise.all(animations.map((a) => a.finished.catch(() => undefined))).then(() => undefined),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, wallClock)
    }),
  ]).then(() => {
    clearTimeout(timer)
  })

  return {
    finished,
    cancel: () => {
      clearTimeout(timer)
      for (const a of animations) {
        try {
          a.cancel()
        } catch {
          // A cancel on an already-finished animation is not a failure worth propagating.
        }
      }
    },
  }
}

function supportsWebAnimations(steps: Array<{ target: Element }>): boolean {
  return steps.every((s) => typeof (s.target as Element & { animate?: unknown }).animate === 'function')
}

/**
 * Write a step's final keyframe onto the element as inline style.
 *
 * This is what makes the reduced-motion path honest rather than a no-op: the
 * element lands exactly where the animation would have left it (`fill:
 * forwards`), it just gets there in zero time.
 */
function commitEndState(step: MotionStep): void {
  const el = step.target
  // SVG elements carry a `.style` too, and 13b's checkmark is one of them.
  if (!(el instanceof HTMLElement) && !(el instanceof SVGElement)) return
  const end = step.keyframes[step.keyframes.length - 1]
  if (!end) return
  if (step.origin) el.style.transformOrigin = step.origin
  for (const [prop, value] of Object.entries(end)) {
    if (prop === 'offset' || prop === 'easing' || prop === 'composite' || value == null) continue
    el.style.setProperty(kebab(prop), String(value))
  }
}

function kebab(prop: string): string {
  return prop.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)
}

/* ── §3 · THE MOMENTS ────────────────────────────────────────────────────── */

/**
 * 13b · COMPLETING A TASK FILES IT.
 *
 * "box fills green (120ms) → checkmark pops scale(.5→1.15→1) on --ease-settle →
 * strikethrough wipes left-to-right (180ms) → label dims → row translates +26px
 * right and COLLAPSES (height, padding, border-width all to 0 in the same
 * stops) → done-log count rolls up one digit." (~900ms total.)
 *
 * THE COLLAPSE IS THE PART THAT BREAKS IF YOU CHEAT IT. §4: height, padding and
 * border-width animate TOGETHER, not `max-height` alone, or the list does not
 * close up — it leaves a hairline and two rows of dead space where the row was.
 * So the row step names all of them in the same keyframes, measured off the row
 * that is actually on screen (a hard-coded 39px would be wrong in two of the
 * three row densities this screen renders).
 *
 * WHAT IS *NOT* HERE, deliberately: the box turning green and the label dimming.
 * Both are colour, both are tokens, and a `var(--color-…)` inside a WAAPI
 * keyframe is not reliably substituted — so those two stops are CSS transitions
 * driven by a `data-filing` attribute on the row, timed by the same tokens. The
 * split is by property type, not by convenience: colour stays in the token file,
 * geometry comes here.
 *
 * The row exits toward the archive (to the RIGHT, where the done log is), so the
 * done log reads as a place rather than a bin. The count roll is the reward.
 * There is no burst, and adding one later is the bug.
 */
export const TASK_FILE_AWAY_MS = 900

/** When the checkmark pops, when the strike wipes, when the row starts leaving. */
const FILE_AWAY_CHECK_AT = DUR_STATE
const FILE_AWAY_STRIKE_AT = 260
const FILE_AWAY_EXIT_AT = 440

export function taskFileAway(parts: {
  row: HTMLElement | null
  /** The checkmark — an `<svg>`, so `Element` rather than `HTMLElement`. */
  check: Element | null
  strike: HTMLElement | null
  /** Resting geometry, measured before the collapse so the keyframes name real pixels. */
  rowHeight: number
  padTop: number
  padBottom: number
  borderTop: number
  borderBottom: number
}): MotionRun {
  const { row, check, strike, rowHeight, padTop, padBottom, borderTop, borderBottom } = parts

  return runSequence([
    // 1 · the checkmark pops. The one place --ease-settle is spent on this screen.
    {
      target: check,
      keyframes: [
        { opacity: 0, transform: 'scale(.5)' },
        { opacity: 1, transform: 'scale(1.15)', offset: 0.55 },
        { opacity: 1, transform: 'scale(1)' },
      ],
      duration: 200,
      delay: FILE_AWAY_CHECK_AT,
      easing: EASE_SETTLE,
    },
    // 2 · the strikethrough wipes left-to-right.
    {
      target: strike,
      keyframes: [{ width: '0%' }, { width: '100%' }],
      duration: DUR_HOVER,
      delay: FILE_AWAY_STRIKE_AT,
      easing: EASE_INK,
    },
    // 3 · the row leaves toward the archive, then closes up. One step, every
    //     box property, so the list never holds a gap that used to be a row.
    {
      target: row,
      keyframes: [
        {
          transform: 'translateX(0)',
          opacity: 1,
          height: `${rowHeight}px`,
          paddingTop: `${padTop}px`,
          paddingBottom: `${padBottom}px`,
          borderTopWidth: `${borderTop}px`,
          borderBottomWidth: `${borderBottom}px`,
          offset: 0,
        },
        {
          transform: 'translateX(26px)',
          opacity: 0,
          height: `${rowHeight}px`,
          paddingTop: `${padTop}px`,
          paddingBottom: `${padBottom}px`,
          borderTopWidth: `${borderTop}px`,
          borderBottomWidth: `${borderBottom}px`,
          offset: 0.55,
        },
        {
          transform: 'translateX(26px)',
          opacity: 0,
          height: '0px',
          paddingTop: '0px',
          paddingBottom: '0px',
          borderTopWidth: '0px',
          borderBottomWidth: '0px',
          offset: 1,
        },
      ],
      duration: TASK_FILE_AWAY_MS - FILE_AWAY_EXIT_AT,
      delay: FILE_AWAY_EXIT_AT,
      easing: EASE_PRESS,
    },
  ])
}

/**
 * 13b's tail · THE COUNT ROLLS UP ONE DIGIT.
 *
 * A translateY of exactly one line-box, so the old digit leaves upward and the
 * new one arrives from below. The reward for finishing something is the data
 * appearing — this is that, and it is the whole celebration budget.
 */
export function countRoll(track: HTMLElement | null, lineHeightPx: number): MotionRun {
  return runSequence([
    {
      target: track,
      keyframes: [{ transform: 'translateY(0)' }, { transform: `translateY(-${lineHeightPx}px)` }],
      duration: 260,
      easing: EASE_PRESS,
    },
  ])
}

/**
 * 13c · SYNC, WITHOUT A SPINNER — one value swapping.
 *
 * "Stale value lifts out (opacity 1→0, translateY(-4px)), fresh value rises in
 * (translateY(5px)→0)."
 *
 * THE RULE IS ABOUT WHAT DOES *NOT* MOVE. Only values that changed animate; a
 * row the connector did not send must be visibly untouched, because that
 * stillness is how the user knows what is new. The caller therefore compares
 * before calling — this function has no opinion about whether a swap is
 * warranted, and calling it on an unchanged value would be the bug.
 */
export function valueSwap(stale: HTMLElement | null, fresh: HTMLElement | null): MotionRun {
  return runSequence([
    {
      target: stale,
      keyframes: [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-4px)' },
      ],
      duration: DUR_HOVER,
      easing: EASE_INK,
    },
    {
      target: fresh,
      keyframes: [
        { opacity: 0, transform: 'translateY(5px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      duration: DUR_HOVER,
      delay: 120,
      easing: EASE_INK,
    },
  ])
}

/**
 * 13c's source dot pings once. The ping itself is a CSS animation (`.mo-ping` in
 * index.css) because its keyframes are pure colour and belong in the token file;
 * this only RE-TRIGGERS it, which CSS cannot do on its own when the class is
 * already there. Reflow-then-re-add is the standard restart, and it is a read of
 * `offsetWidth`, not a layout the user can see.
 *
 * One ping, then nothing. Not a repeating pulse — a pulse means "still waiting",
 * and this fires because something already arrived.
 */
export function sourcePing(dot: HTMLElement | null): void {
  if (!dot || prefersReducedMotion()) return
  dot.classList.remove('mo-ping')
  void dot.offsetWidth
  dot.classList.add('mo-ping')
}

/**
 * 13d · THE BLOCK LANDS.
 *
 * "On drop the block scales up from `transform-origin: top` only."
 *
 * TOP-ORIGIN IS THE WHOLE RULE, not a flourish: growing from the centre would
 * make the new block visually cover the event above it for ~200ms, which is the
 * screen showing a collision that is not there.
 */
export function blockLands(block: HTMLElement | null): MotionRun {
  return runSequence([
    {
      target: block,
      origin: 'top',
      keyframes: [
        { opacity: 0, transform: 'scaleY(.9)' },
        { opacity: 1, transform: 'scaleY(1.02)', offset: 0.6 },
        { opacity: 1, transform: 'scaleY(1)' },
      ],
      duration: 260,
      easing: EASE_PRESS,
    },
  ])
}

/**
 * 13e · CHAT STREAMS, THEN CITES.
 *
 * "Reveal is LINE-BY-LINE (`steps()` width wipe per line), not
 * character-by-character, so the eye reads whole clauses."
 *
 * WHAT THIS CODEBASE CAN AND CANNOT DO WITH THAT, said plainly. The reference
 * draws three pre-broken lines of fixed width and wipes each one open. A real
 * answer here is prose in a `max-w-[760px]` paragraph that wraps wherever the
 * viewport says, and JS does not know where the visual line breaks are without
 * measuring text runs. A width wipe over a wrapped run clips the wrong axis and
 * reads as a collapsing box.
 *
 * So the unit of reveal is the CLAUSE — a sentence — which is what the brief
 * says the mechanic is FOR ("so the eye reads whole clauses"), and each clause
 * arrives whole rather than fading in half-legible. `steps(1)` is what makes it
 * arrive rather than fade: a partially-opaque sentence is exactly the
 * character-by-character feel the brief rejects, one level up.
 *
 * `splitClauses` is exported so the split is testable, and so nothing else in
 * the app invents a second definition of "a clause".
 */
export function splitClauses(text: string): string[] {
  const parts = text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
  // A single unbroken run is still one clause. Returning [] here would render an
  // answer as nothing at all, which is the worst possible failure on this screen.
  return parts.length > 0 ? parts : [text]
}

/** How long a clause is held back before it arrives. Longer clause, longer beat. */
export function clauseDelay(chars: number): number {
  return Math.max(220, Math.min(chars * 11, 900))
}

export function clauseArrive(clause: Element | null, delay: number): MotionStep {
  return {
    target: clause,
    keyframes: [{ opacity: 0 }, { opacity: 1 }],
    duration: 1,
    delay,
    easing: 'steps(1)',
  }
}

/** Run a whole answer's reveal. Resolves when the last clause is on screen. */
export function streamClauses(clauses: Array<Element | null>): MotionRun {
  let at = 0
  return runSequence(
    clauses.map((node, i) => {
      const step = clauseArrive(node, at)
      at += clauseDelay((clauses[i] as HTMLElement | null)?.textContent?.length ?? 40)
      return step
    }),
  )
}

/**
 * 13e's tail · the citation chips.
 *
 * They pop AFTER the final line completes and never before. A chip that appears
 * mid-claim implies the model is citing something it has not said yet — the
 * timing here is a truth claim, not a taste call.
 */
export function citationsPop(chips: HTMLElement | null, delay: number): MotionRun {
  return runSequence([
    {
      target: chips,
      keyframes: [
        { opacity: 0, transform: 'translateY(6px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      duration: 260,
      delay,
      easing: EASE_SETTLE,
    },
  ])
}

/**
 * 13f · A GRADE LANDS.
 *
 * "The score rises translateY(9px)→0 with the class median appearing in the SAME
 * BEAT underneath it." One step, one element wrapping both, because a score that
 * arrives a moment before its context is a score without context.
 *
 * (What this codebase can put underneath the number is whatever the model
 * actually carries — Canvas returns no class median, and inventing one would be
 * the app faking knowledge. The beat is the same either way.)
 */
export function gradeRise(scoreBlock: HTMLElement | null): MotionRun {
  return runSequence([
    {
      target: scoreBlock,
      keyframes: [
        { opacity: 0, transform: 'translateY(9px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      duration: 280,
      easing: EASE_PRESS,
    },
  ])
}

/**
 * 13a · CAPTURE RESOLVES AS YOU TYPE — the stagger between chips.
 *
 * "one chip at a time (~80ms apart), as the parser commits."
 * If a field cannot be parsed NO CHIP APPEARS; the absence is the feedback. That
 * rule lives in the caller (it renders nothing), not here — this only spaces out
 * the chips that a parse actually produced.
 */
export const CHIP_STAGGER_MS = 80

export function chipEnter(chip: HTMLElement | null, index: number): MotionRun {
  return runSequence([
    {
      target: chip,
      keyframes: [
        { opacity: 0, transform: 'translateY(5px) scale(.96)' },
        { opacity: 1, transform: 'translateY(0) scale(1.02)', offset: 0.65 },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      duration: 220,
      delay: index * CHIP_STAGGER_MS,
      easing: EASE_SETTLE,
    },
  ])
}

/* ── THE DAY PAGE (14a–14h) ──────────────────────────────────────────────────
   docs/design/handoff/day-page/MotionSpec.dc.html is the contract; Motion.dc.html plays it. The
   beats below are that board's own, named here so no Day component types a duration. Their
   CSS twins (`--dur-day-*`, `--dur-undo`) live in index.css's Day block. */

/** 14a · Done: tile fills green (0) → strike wipes (240) → row leaves (420) → closes (560). ~800 ms. */
export const DAY_DONE_STRIKE_AT = 240
export const DAY_DONE_LEAVE_AT = 420
export const DAY_DONE_GONE_AT = 560
/** 14b · Dismiss: title and why dim (0) → row leaves (120) → closes (260). ~500 ms. */
export const DAY_DISMISS_LEAVE_AT = DUR_STATE
export const DAY_DISMISS_GONE_AT = 260
/** 14c · Move or snooze: the row slides out 140 ms, then closes. */
export const DAY_MOVE_GONE_AT = 140
/** Height, padding and border close together. */
export const DAY_COLLAPSE_MS = 240
/** 14c · the receiving pile's single violet wash. */
export const DAY_WASH_MS = 900
/** 14d / 14e · a returning or newly shown row keeps its source edge this long. */
export const DAY_EDGE_MS = 1200
/**
 * 14d · THE ONE NAMED ADDITION: the undo window, drawn as a 5 s LINEAR drain on the toast. It is a
 * deadline, not decoration, so reduced motion does not shorten it.
 */
export const UNDO_WINDOW_MS = 5000
